import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, Check, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import api, { formatApiError } from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";

const HERO_BG = "https://images.unsplash.com/photo-1762279389083-abf71f22d338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkYXRhJTIwYW5hbHl0aWNzJTIwY2hhcnQlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzgzMTI2MjR8MA&ixlib=rb-4.1.0&q=85";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  return (
    <Shell title="Forgot password" subtitle="We'll email you a secure link to choose a new one." testid="forgot">
      {sent ? (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-4" data-testid="forgot-sent">
          <div className="w-10 h-10 rounded-sm bg-[#10B981]/10 border border-[#10B981]/30 grid place-items-center">
            <Mail className="w-5 h-5 text-[#10B981]" />
          </div>
          <h3 className="font-display text-xl font-medium">Check your inbox</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            If an account exists for <strong className="text-zinc-200">{email}</strong>, a password reset link is on its way. The link expires in 60 minutes.
          </p>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Didn't get it? Check spam, or wait 30 seconds and try again. Some email providers can take a minute.
          </p>
          <Button asChild variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="forgot-back-login">
            <Link to="/login"><ArrowLeft className="w-4 h-4 mr-2" /> Back to login</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="forgot-email" />
          </div>
          {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="forgot-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="forgot-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
          </Button>
          <div className="text-center">
            <Link to="/login" className="text-sm text-zinc-500 hover:text-zinc-200 transition-colors inline-flex items-center gap-1.5" data-testid="forgot-back-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to login
            </Link>
          </div>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <Shell title="Invalid link" subtitle="This reset link looks malformed." testid="reset-invalid">
        <p className="text-zinc-400 text-sm leading-relaxed">Please request a new password reset email.</p>
        <Button asChild className="mt-6 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="reset-request-new">
          <Link to="/forgot-password">Request new link</Link>
        </Button>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password" subtitle="Pick something strong you'll remember." testid="reset">
      {done ? (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-3" data-testid="reset-done">
          <div className="w-10 h-10 rounded-sm bg-[#10B981]/10 border border-[#10B981]/30 grid place-items-center">
            <Check className="w-5 h-5 text-[#10B981]" />
          </div>
          <h3 className="font-display text-xl font-medium">Password updated</h3>
          <p className="text-zinc-400 text-sm">Redirecting you to login…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">New password</Label>
            <PasswordInput required minLength={6} value={next} onChange={(e) => setNext(e.target.value)}
                           className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="reset-new" />
            <p className="text-xs text-zinc-600 mt-1.5">Min 6 characters.</p>
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Confirm new password</Label>
            <PasswordInput required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                           className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="reset-confirm" />
          </div>
          {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="reset-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="reset-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" /> Update password</>}
          </Button>
        </form>
      )}
    </Shell>
  );
}

function Shell({ children, title, subtitle, testid }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090B]">
      <div className="hidden lg:flex relative border-r border-zinc-800/80 overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#09090B] via-[#09090B]/40 to-[#0055FF]/20" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center text-white font-bold">⚡</div>
            <span className="font-display font-semibold text-lg">RankPilot AI</span>
          </Link>
          <div>
            <span className="label-eyebrow">Security</span>
            <p className="font-display text-3xl mt-3 max-w-md leading-tight">
              "Reset links expire in 60 minutes and can only be used once — no shared-link replay attacks."
            </p>
            <div className="mt-6 text-sm text-zinc-500">— RankPilot security team</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 py-12 lg:px-16">
        <div className="max-w-md mx-auto w-full" data-testid={`${testid}-page`}>
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-12">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center text-white font-bold">⚡</div>
            <span className="font-display font-semibold text-lg">RankPilot AI</span>
          </Link>
          <span className="label-eyebrow">{testid.startsWith("reset") ? "Reset" : "Recover"}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2 mb-2">{title}</h1>
          <p className="text-zinc-400 mb-8">{subtitle}</p>
          {children}
          <SiteFooter variant="minimal" />
        </div>
      </div>
    </div>
  );
}
